import { useLocation } from "react-router";
import { useEffect, useRef } from "react";
import { USER_ACTIVITY_TRACKING_KEY } from "~/utils/constant";

export interface TabSwitch {
  leftTabAt: number; // Unix timestamp when user left tab
  returnedAt: number | null; // Unix timestamp when user returned (null if ongoing)
  duration: number; // Time away from tab in milliseconds
}

export interface ActivitySession {
  date: string; // YYYY-MM-DD format
  sessionId: string; // Unique session identifier
  startTime: number; // Timestamp when user started browsing
  endTime: number | null; // Timestamp when user ended session
  pages: PageActivity[];
  tabSwitches?: TabSwitch[]; // Track when user switches tabs
}

export interface PageActivity {
  page: string; // Route path
  title: string; // Page title
  enteredAt: number; // Unix timestamp when entered
  exitedAt: number | null; // Unix timestamp when exited
  timeSpent: number; // Time spent in milliseconds
  tabSwitches?: TabSwitch[]; // Tab switches while on this page
  activeTime: number; // Time actually active on page (excluding tab switches)
}

export interface UserActivityData {
  sessions: ActivitySession[];
  lastActivity: number; // Last activity timestamp
}

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity = new session

export const useActivityTracking = () => {
  const location = useLocation();
  const sessionRef = useRef<string>("");
  const pageEntryTimeRef = useRef<number>(0);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Initialize or get activity data
    const initializeActivityTracking = () => {
      const storedData = sessionStorage.getItem(USER_ACTIVITY_TRACKING_KEY);
      const activityData: UserActivityData = storedData ? JSON.parse(storedData) : { sessions: [], lastActivity: Date.now() };

      const today = new Date().toISOString().split("T")[0];
      const todaySessionIndex = activityData.sessions.findIndex((s) => s.date === today && s.endTime === null);
      let currentSession: ActivitySession | undefined = todaySessionIndex >= 0 ? activityData.sessions[todaySessionIndex] : undefined;

      // Check for session timeout (if last activity was more than 30 minutes ago)
      const timeSinceLastActivity = Date.now() - activityData.lastActivity;
      if (currentSession && timeSinceLastActivity > SESSION_TIMEOUT) {
        // End the previous session
        currentSession.endTime = activityData.lastActivity;
        currentSession = undefined;
      }

      // Create new session if needed
      if (!currentSession) {
        const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newSession: ActivitySession = {
          date: today,
          sessionId,
          startTime: Date.now(),
          endTime: null,
          pages: [],
        };
        activityData.sessions.push(newSession);
        currentSession = newSession;
      }

      return { activityData, currentSession, today };
    };

    // Record page view
    const recordPageView = () => {
      const { activityData, currentSession } = initializeActivityTracking();

      if (!currentSession) return;

      const currentPath = location.pathname;
      const pageTitle = document.title || currentPath;
      const now = Date.now();

      // End previous page activity if exists
      const lastPage = currentSession.pages[currentSession.pages.length - 1];
      if (lastPage && !lastPage.exitedAt) {
        lastPage.exitedAt = now;
        lastPage.timeSpent = now - lastPage.enteredAt;
        // Initialize activeTime if not exists
        if (lastPage.activeTime === undefined) {
          lastPage.activeTime = lastPage.timeSpent;
        }
      }

      // Check if we're just re-rendering the same page
      if (lastPage?.page === currentPath) {
        // Update the last activity time without creating duplicate
        activityData.lastActivity = now;
      } else {
        // Add new page activity
        const newPage: PageActivity = {
          page: currentPath,
          title: pageTitle,
          enteredAt: now,
          exitedAt: null,
          timeSpent: 0,
          tabSwitches: [],
          activeTime: 0,
        };
        currentSession.pages.push(newPage);
      }

      // Update last activity
      activityData.lastActivity = now;

      // Save to sessionStorage
      sessionStorage.setItem(USER_ACTIVITY_TRACKING_KEY, JSON.stringify(activityData));

      // Store session ID for this session
      sessionRef.current = currentSession.sessionId;
      pageEntryTimeRef.current = now;
    };

    recordPageView();

    // Handle visibility change (when user tabs away)
    const handleVisibilityChange = () => {
      const storedData = sessionStorage.getItem(USER_ACTIVITY_TRACKING_KEY);
      if (!storedData) return;

      const activityData: UserActivityData = JSON.parse(storedData);
      const currentSession = activityData.sessions.find((s) => s.sessionId === sessionRef.current && s.endTime === null);

      if (!currentSession) return;

      const lastPage = currentSession.pages[currentSession.pages.length - 1];
      if (!lastPage) return;

      if (document.hidden) {
        // User left the tab
        const leftTabAt = Date.now();

        // Initialize tabSwitches array if it doesn't exist
        if (!lastPage.tabSwitches) {
          lastPage.tabSwitches = [];
        }

        // Record tab switch event
        const tabSwitch: TabSwitch = {
          leftTabAt,
          returnedAt: null,
          duration: 0,
        };
        lastPage.tabSwitches.push(tabSwitch);

        // Also track at session level
        if (!currentSession.tabSwitches) {
          currentSession.tabSwitches = [];
        }
        currentSession.tabSwitches.push(tabSwitch);

        sessionStorage.setItem(USER_ACTIVITY_TRACKING_KEY, JSON.stringify(activityData));
      } else {
        // User returned to the tab
        if (lastPage.tabSwitches && lastPage.tabSwitches.length > 0) {
          const lastTabSwitch = lastPage.tabSwitches[lastPage.tabSwitches.length - 1];
          if (lastTabSwitch && !lastTabSwitch.returnedAt) {
            lastTabSwitch.returnedAt = Date.now();
            lastTabSwitch.duration = lastTabSwitch.returnedAt - lastTabSwitch.leftTabAt;
          }
        }

        // Also update session level tab switch
        if (currentSession.tabSwitches && currentSession.tabSwitches.length > 0) {
          const lastSessionTabSwitch = currentSession.tabSwitches[currentSession.tabSwitches.length - 1];
          if (lastSessionTabSwitch && !lastSessionTabSwitch.returnedAt) {
            lastSessionTabSwitch.returnedAt = Date.now();
            lastSessionTabSwitch.duration = lastSessionTabSwitch.returnedAt - lastSessionTabSwitch.leftTabAt;
          }
        }

        const timeSinceLastActivity = Date.now() - activityData.lastActivity;

        // If more than 30 minutes, treat as new session
        if (timeSinceLastActivity > SESSION_TIMEOUT) {
          recordPageView();
        } else {
          // Continue in current session
          if (lastPage && lastPage.exitedAt) {
            lastPage.exitedAt = null; // Resume tracking
          }
        }

        activityData.lastActivity = Date.now();
        sessionStorage.setItem(USER_ACTIVITY_TRACKING_KEY, JSON.stringify(activityData));
      }
    };

    // Handle before unload (when user closes tab/window)
    const handleBeforeUnload = () => {
      const storedData = sessionStorage.getItem(USER_ACTIVITY_TRACKING_KEY);
      if (storedData) {
        const activityData: UserActivityData = JSON.parse(storedData);
        const currentSession = activityData.sessions.find((s) => s.sessionId === sessionRef.current && s.endTime === null);

        if (currentSession) {
          const lastPage = currentSession.pages[currentSession.pages.length - 1];
          if (lastPage && !lastPage.exitedAt) {
            lastPage.exitedAt = Date.now();
            lastPage.timeSpent = lastPage.exitedAt - lastPage.enteredAt;
            // Initialize activeTime if not exists
            if (lastPage.activeTime === undefined) {
              lastPage.activeTime = lastPage.timeSpent;
            }
          }
          currentSession.endTime = Date.now();
          sessionStorage.setItem(USER_ACTIVITY_TRACKING_KEY, JSON.stringify(activityData));
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [location]);

  // Function to get activity data
  const getActivityData = (): UserActivityData | null => {
    if (typeof window === "undefined") return null;
    const data = sessionStorage.getItem(USER_ACTIVITY_TRACKING_KEY);
    return data ? JSON.parse(data) : null;
  };

  // Function to get today's activity
  const getTodayActivity = (): ActivitySession | undefined => {
    const data = getActivityData();
    if (!data) return undefined;
    const today = new Date().toISOString().split("T")[0];
    return data.sessions.find((s) => s.date === today);
  };

  // Function to get stats for a specific date
  const getActivityByDate = (date: string): ActivitySession | undefined => {
    const data = getActivityData();
    if (!data) return undefined;
    return data.sessions.find((s) => s.date === date);
  };

  // Function to get total time spent in session
  const getTotalSessionTime = (session: ActivitySession): number => {
    if (!session.endTime) return 0;
    return session.endTime - session.startTime;
  };

  // Function to get total time spent on a page
  const getPageTimeSpent = (session: ActivitySession, page: string): number => {
    return session.pages.filter((p) => p.page === page).reduce((total, p) => total + (p.timeSpent || 0), 0);
  };

  // Function to clear all activity data
  const clearActivityData = () => {
    sessionStorage.removeItem(USER_ACTIVITY_TRACKING_KEY);
  };

  return {
    getActivityData,
    getTodayActivity,
    getActivityByDate,
    getTotalSessionTime,
    getPageTimeSpent,
    clearActivityData,
  };
};

export default useActivityTracking;
